import { computed, onMounted, ref } from 'vue'

export interface LocalizedStrings {
  en: string
  fa: string
}

export interface WhitelabelConfig {
  name: LocalizedStrings
}

const WHITELABEL_STORAGE_KEY = 'whitelabel_config'

export const useWhitelabel = () => {
  // Use hardcoded default values
  const defaultConfig = {
    name: {
      en: 'BitTrip',
      fa: 'بیت‌تریپ'
    }
  }

  // Initialize reactive state
  const whitelabelConfig = ref<WhitelabelConfig>(defaultConfig)

  const isLoading = ref(false)

  // Get current locale
  const { $i18n } = useNuxtApp()
  const currentLocale = computed(() => $i18n.locale.value)

  const app = computed(() => ({
    name: whitelabelConfig.value.name[currentLocale.value] || whitelabelConfig.value.name.en
  }))

  const loadFromLocalStorage = (): WhitelabelConfig | null => {
    if (import.meta.client) {
      try {
        const stored = localStorage.getItem(WHITELABEL_STORAGE_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          return parsed.config
        }
      } catch (error) {
        console.warn('Failed to load whitelabel config from localStorage:', error)
      }
    }
    return null
  }

  const saveToLocalStorage = (config: WhitelabelConfig) => {
    if (import.meta.client) {
      try {
        localStorage.setItem(WHITELABEL_STORAGE_KEY, JSON.stringify(config))
      } catch (error) {
        console.warn('Failed to save whitelabel config to localStorage:', error)
      }
    }
  }

  const fetchWhitelabelConfig = async (): Promise<WhitelabelConfig> => {
    try {
      return await $fetch<WhitelabelConfig>('/api/whitelabel')
    } catch (error) {
      console.warn('Failed to fetch whitelabel config:', error)
      // Return default config on error
      return defaultConfig
    }
  }

  const loadWhitelabelConfig = async () => {
    if (isLoading.value) return

    isLoading.value = true

    try {
      // First, try to load from localStorage
      const cachedConfig = loadFromLocalStorage()
      if (cachedConfig) {
        whitelabelConfig.value = cachedConfig
      }

      // Always fetch fresh config from API (but don't wait for it to complete)
      const freshConfig = await fetchWhitelabelConfig()
      whitelabelConfig.value = freshConfig
      saveToLocalStorage(freshConfig)
    } finally {
      isLoading.value = false
    }
  }

  // Load config on mount
  onMounted(() => {
    void loadWhitelabelConfig()
  })

  return {
    app: readonly(app),
    isLoading: readonly(isLoading),
    loadWhitelabelConfig
  }
}
